// ═══════════════════════════════════════════════════════════
// PARTICLE SYSTEM - Visual Juice!
// ═══════════════════════════════════════════════════════════

import { Particle, Vector2, ScorePopup, CornerType, Bounds } from './types.js';
import { Utils } from './Utils.js';
import { Theme } from './Theme.js';

export class ParticleSystem {
    private particles: Particle[] = [];
    private scorePopups: ScorePopup[] = [];
    private bounds: Bounds;

    constructor(bounds: Bounds) {
        this.bounds = bounds;
    }

    update(deltaTime: number): void {
        // Update particles
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];

            // Update position
            p.position.x += p.velocity.x * deltaTime;
            p.position.y += p.velocity.y * deltaTime;

            // Apply gravity (from theme)
            p.velocity.y += 400 * Theme.particles.gravityMultiplier * deltaTime;

            // Apply friction (from theme)
            p.velocity.x *= Theme.particles.frictionMultiplier;
            p.velocity.y *= Theme.particles.frictionMultiplier;

            // Update life
            p.life -= deltaTime;

            // Update alpha based on life
            p.alpha = Utils.clamp(p.life / p.maxLife, 0, 1);

            // Remove dead particles
            if (p.life <= 0) {
                this.particles.splice(i, 1);
            }
        }

        // Update score popups
        for (let i = this.scorePopups.length - 1; i >= 0; i--) {
            const popup = this.scorePopups[i];

            popup.position.x += popup.velocity.x * deltaTime;
            popup.position.y += popup.velocity.y * deltaTime;

            popup.life -= deltaTime;

            if (popup.life <= 0) {
                this.scorePopups.splice(i, 1);
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        const offsetY = this.bounds.offsetY || 0;

        // Render particles with enhanced neon glow
        for (const p of this.particles) {
            ctx.save();
            ctx.globalAlpha = p.alpha;

            // Parse color for gradient
            const r = parseInt(p.color.slice(1, 3), 16);
            const g = parseInt(p.color.slice(3, 5), 16);
            const b = parseInt(p.color.slice(5, 7), 16);

            // Apply offset for rendering
            const renderY = p.position.y + offsetY;

            // Draw particle with glow
            const gradient = ctx.createRadialGradient(
                p.position.x, renderY, 0,
                p.position.x, renderY, p.size * 2
            );
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
            gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.6)`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.fillStyle = gradient;
            ctx.fillRect(
                p.position.x - p.size * 2,
                renderY - p.size * 2,
                p.size * 4,
                p.size * 4
            );

            ctx.restore();
        }

        // Render score popups with neon glow
        for (const popup of this.scorePopups) {
            ctx.save();

            const progress = 1 - (popup.life / popup.maxLife);
            const alpha = popup.life / popup.maxLife;
            const scale = 1 + Utils.easeOutBack(Math.min(progress * 3, 1)) * 0.7;

            ctx.globalAlpha = alpha;
            ctx.font = `bold ${Theme.ui.comboTextSize * 0.5 * scale}px "Courier New", monospace`;
            ctx.fillStyle = popup.color;
            ctx.strokeStyle = Theme.ui.popupStroke;
            ctx.lineWidth = Theme.ui.popupStrokeWidth;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Add glow effect
            ctx.shadowColor = popup.color;
            ctx.shadowBlur = 20;

            const popupRenderY = popup.position.y + offsetY;
            ctx.strokeText(popup.text, popup.position.x, popupRenderY);
            ctx.fillText(popup.text, popup.position.x, popupRenderY);

            ctx.restore();
        }
    }

    // Emit particles for corner hit (enhanced with theme)
    emitCornerHit(position: Vector2, count: number = Theme.particles.cornerHit): void {
        const colors = Theme.particles.colors;

        for (let i = 0; i < count; i++) {
            const angle = (Math.PI * 2 * i) / count + Utils.random(-0.1, 0.1);
            const speed = Utils.random(250, 500); // Faster, more energetic

            this.particles.push({
                position: { x: position.x, y: position.y },
                velocity: {
                    x: Math.cos(angle) * speed,
                    y: Math.sin(angle) * speed
                },
                life: Utils.random(Theme.particles.minLife, Theme.particles.maxLife),
                maxLife: Theme.particles.maxLife,
                size: Utils.random(Theme.particles.minSize, Theme.particles.maxSize),
                color: Utils.randomChoice(colors),
                alpha: 1.0
            });
        }
    }

    // Emit particles for wall bounce (enhanced)
    emitBounce(position: Vector2, normal: Vector2): void {
        const count = Theme.particles.wallBounce;
        const baseAngle = Math.atan2(normal.y, normal.x);

        for (let i = 0; i < count; i++) {
            const spread = Math.PI / 3;
            const angle = baseAngle + Utils.random(-spread, spread);
            const speed = Utils.random(150, 250); // Faster

            this.particles.push({
                position: { x: position.x, y: position.y },
                velocity: {
                    x: Math.cos(angle) * speed,
                    y: Math.sin(angle) * speed
                },
                life: Utils.random(0.3, 0.6),
                maxLife: 0.6,
                size: Utils.random(Theme.particles.minSize, Theme.particles.maxSize * 0.7),
                color: Theme.colors.neonCyan,
                alpha: 1.0
            });
        }
    }

    // Add score popup (enhanced with theme colors)
    addScorePopup(position: Vector2, score: number, isPerfect: boolean = false, cornerType?: CornerType): void {
        const text = isPerfect ? `PERFECT! +${score}` : `+${score}`;
        const color = isPerfect ? Theme.ui.popupColor : Theme.colors.neonCyan;

        // Calculate offset and velocity based on corner type to keep text visible
        let offsetX = 0;
        let offsetY = 0;
        let velocityX = 0;
        let velocityY = -50; // Default upward movement

        if (cornerType) {
            // Offset distance from corner (text needs about 60px width, 30px height)
            const offsetDistance = 80;

            switch (cornerType) {
                case CornerType.TOP_LEFT:
                    // Move right and down from top-left corner
                    offsetX = offsetDistance;
                    offsetY = offsetDistance / 2;
                    velocityX = 30;
                    velocityY = -30;
                    break;
                case CornerType.TOP_RIGHT:
                    // Move left and down from top-right corner
                    offsetX = -offsetDistance;
                    offsetY = offsetDistance / 2;
                    velocityX = -30;
                    velocityY = -30;
                    break;
                case CornerType.BOTTOM_LEFT:
                    // Move right and up from bottom-left corner
                    offsetX = offsetDistance;
                    offsetY = -offsetDistance / 2;
                    velocityX = 30;
                    velocityY = -70;
                    break;
                case CornerType.BOTTOM_RIGHT:
                    // Move left and up from bottom-right corner
                    offsetX = -offsetDistance;
                    offsetY = -offsetDistance / 2;
                    velocityX = -30;
                    velocityY = -70;
                    break;
            }
        }

        this.scorePopups.push({
            position: { x: position.x + offsetX, y: position.y + offsetY },
            text: text,
            life: 1.5,
            maxLife: 1.5,
            velocity: { x: velocityX, y: velocityY },
            color: color
        });
    }

    // Clear all particles
    clear(): void {
        this.particles = [];
        this.scorePopups = [];
    }

    getParticleCount(): number {
        return this.particles.length;
    }
}
